import { formatTimeAgo, useTimeAgo, UseTimeAgoMessages, UseTimeAgoUnitNamesDefault } from '@vueuse/core'
import { MaybeRef } from 'vue'
import { $tc, $t } from '@/app-bootstrapping/SetUpInternationalization'

type DateLike = Date | string | number

type LocaleTimeAgoOptions = {
  // Adds "ago" to past times, for example "3 days ago" rather than "3 days".
  withAgo?: boolean
}

// Shamelessly stolen from https://github.com/vueuse/vueuse/issues/1592 and improved
// A simple localized version of vueuse's useTimeAgo
// Built per call: $t must not run at module import time, before the locale is set up.
function localeTimeAgoOptions({ withAgo = false }: LocaleTimeAgoOptions = {}) {
  const I18N_MESSAGES: UseTimeAgoMessages<UseTimeAgoUnitNamesDefault> = {
    justNow: $t('just now'),
    past: (n) =>
      n.match(/\d/) ? (withAgo ? $t(':timeString ago', { timeString: n }) : $t(':timeString', { timeString: n })) : n,
    future: (n) => (n.match(/\d/) ? $t('in :timeString', { timeString: n }) : n),
    month: (n, past) =>
      n === 1
        ? past
          ? $t('last month')
          : $t('next month')
        : $tc(`:time month | :time months`, n, { time: n }),
    year: (n, past) =>
      n === 1
        ? past
          ? $t('last year')
          : $t('next year')
        : $tc(`:time year | :time years`, n, { time: n }),
    day: (n, past) =>
      n === 1
        ? past
          ? $t('yesterday')
          : $t('tomorrow')
        : $tc(`:time day | :time days`, n, { time: n }),
    week: (n, past) =>
      n === 1
        ? past
          ? $t('last week')
          : $t('next week')
        : $tc(`:time week | :time weeks`, n, { time: n }),
    hour: (n) => $tc(':time hour | :time hours', n, { time: n }),
    minute: (n) => $tc(':time minute | :time minutes', n, { time: n }),
    second: (n) => $tc(`:time second | :time seconds`, n, { time: n }),
    invalid: '',
  }

  return {
    fullDateFormatter: (d: Date) => d.toLocaleDateString(),
    messages: I18N_MESSAGES,
  }
}

export function useLocaleTimeAgo(date: MaybeRef<DateLike>, options: LocaleTimeAgoOptions = {}) {
  return useTimeAgo(date, localeTimeAgoOptions(options))
}

/** Pure variant, safe inside computed(): no reactive timer per call. */
export function formatLocaleTimeAgo(date: DateLike, options: LocaleTimeAgoOptions = {}): string {
  return formatTimeAgo(new Date(date), localeTimeAgoOptions(options))
}
